import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import {
  finalizeAllianceTaskPeriod,
  getAllianceWarTargetMembers,
  type AllianceTaskOptionId,
  type AllianceTaskPeriodClient,
  type AllianceWarTargetMember,
  type AttackTargetInput,
} from '../../common/allianceApi'
import { ALLIANCE_TASK_ATTACK_COUNTS, ALLIANCE_TASK_TITLE_STYLE } from '../../common/allianceTaskOptionsClient'
import type { CastleData } from '../../game/world-map/terrial-engine/types'
import { CastlePickerModal, DateTimeField } from './allianceAttackPlanShared'
import { alert } from '../Alert/Alert'

type AttackOptionId = Extract<AllianceTaskOptionId, 'conquer' | 'raid'>

interface AllianceAttackPlanModalProps {
  open: boolean
  address: string
  /** The leader's own alliance id — to load our members for the attacker picker. */
  allianceId?: string
  optionId: AttackOptionId
  period: AllianceTaskPeriodClient
  onBack: () => void
  onFinalized: () => void
}

interface TargetRow {
  allianceId: string
  allianceName: string
  memberWallet: string
  ownerName: string
  castleId: string
  attackerWallets: string[]
  mainAttackerWallet: string
  from: string // datetime-local value
  to: string
}

const AT = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/alliance/alliance-task'
const MODAL_BG = `${AT}/modal-bg.webp`
const CASTLE_ICON = `${AT}/castle-icon.webp`
const TAB_ACTIVE = `${AT}/tab-active-bg.webp`
const TAB_INACTIVE = `${AT}/tab-inactive-bg.webp`
const TEXT_BG = `${AT}/text-bg.webp`
const CONFIRM_BTN = `${AT}/confirm-btn.webp`
const BACK_BTN = `${AT}/back-btn.webp`
const TITLE_BG = `${AT}/title-bg.webp`
const CINZEL = '"Cinzel", "Trajan Pro", "Times New Roman", serif'

function pad(n: number): string {
  return String(n).padStart(2, '0')
}
/** Date → `YYYY-MM-DDTHH:mm` in local time (for <input type="datetime-local">). */
function toLocalInput(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Small label above each field (uppercase antique gold, matches the reference art). */
const labelStyle: React.CSSProperties = {
  fontFamily: CINZEL,
  fontWeight: 700,
  fontSize: '2cqw',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#5a3c17',
}

/* ─────────────────────────────────────────────────────────────────────────
 * A single attacker card (avatar + name + KPS). Selected → gold check;
 * the main attacker gets a filled star ribbon.
 * ──────────────────────────────────────────────────────────────────────── */
const AttackerCard: React.FC<{
  member: AllianceWarTargetMember
  picked: boolean
  isMain: boolean
  disabled: boolean
  onToggle: () => void
  onSetMain: () => void
}> = ({ member, picked, isMain, disabled, onToggle, onSetMain }) => {
  const { t } = useTranslation()
  const initial = (member.nickName || '?').trim().charAt(0).toUpperCase()
  return (
    <div
      className="relative shrink-0"
      style={{
        // Sized to hold three comfortable lines (name / KPS / MAIN ATTACKER) without the text
        // crowding the plate's border art or sliding under the check + star badges on the right.
        width: '36cqw',
        height: '13cqw',
        overflow: 'visible',
        backgroundImage: `url(${TEXT_BG})`,
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat',
        filter: picked
          ? 'brightness(1.08) drop-shadow(0 0 6px rgba(240,212,136,0.85))'
          : 'brightness(0.86)',
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        className="relative flex h-full w-full items-center disabled:cursor-not-allowed"
        // Left clears the overlapping avatar; right reserves the badge column.
        style={{ paddingLeft: '11.8cqw', paddingRight: '7.5cqw' }}
      >
        {/* Circular avatar — about the card height, overlapping its left edge. */}
        <span
          className="absolute flex items-center justify-center overflow-hidden"
          style={{
            left: '-2.9cqw',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '13.8cqw',
            height: '13.8cqw',
            borderRadius: '50%',
            border: `0.55cqw solid ${picked ? '#e6cf8a' : 'rgba(201,162,75,0.55)'}`,
            background: '#241a10',
            boxShadow: '0 2px 5px rgba(0,0,0,0.5)',
            zIndex: 2,
          }}
        >
          {member.avatarUrl ? (
            <img src={member.avatarUrl} alt="" draggable={false} className="h-full w-full object-cover" />
          ) : (
            <span style={{ fontFamily: CINZEL, fontWeight: 800, fontSize: '5.3cqw', color: '#caa24b' }}>{initial}</span>
          )}
        </span>
        <span className="min-w-0 flex-1 text-left">
          <span
            className="block truncate"
            style={{ fontFamily: CINZEL, fontWeight: 800, fontSize: '3cqw', lineHeight: 1.1, color: '#f3ead0' }}
          >
            {member.nickName}
          </span>
          <span
            className="block truncate"
            style={{ fontFamily: CINZEL, fontWeight: 600, fontSize: '2.4cqw', lineHeight: 1.2, color: '#d8b877', marginTop: '0.55cqw' }}
          >
            {t('alliance.conquerKps', { defaultValue: 'KPS {{n}}', n: (member.kps ?? 0).toLocaleString() })}
          </span>
          {isMain ? (
            <span
              className="block truncate"
              style={{ fontFamily: CINZEL, fontWeight: 700, fontSize: '2cqw', lineHeight: 1.2, letterSpacing: '0.06em', color: '#ffdf9e', marginTop: '0.55cqw' }}
            >
              ★ {t('alliance.conquerMainAttacker', { defaultValue: 'MAIN ATTACKER' })}
            </span>
          ) : null}
        </span>
      </button>

      {/* selected check (top-right) */}
      {picked ? (
        <span
          className="pointer-events-none absolute flex items-center justify-center rounded-full"
          style={{ top: '1.5cqw', right: '1.8cqw', width: '4.4cqw', height: '4.4cqw', background: '#caa24b', color: '#1a120b', fontSize: '2.9cqw', fontWeight: 900 }}
        >
          ✓
        </span>
      ) : null}

      {/* set-main star (only on selected cards) */}
      {picked ? (
        <button
          type="button"
          onClick={onSetMain}
          disabled={disabled}
          title={t('alliance.conquerSetMain', { defaultValue: 'Set as main attacker' }) as string}
          className="absolute flex items-center justify-center rounded-full transition-[filter] hover:brightness-110"
          style={{
            bottom: '1.5cqw',
            right: '1.8cqw',
            width: '4.8cqw',
            height: '4.8cqw',
            fontSize: '2.9cqw',
            background: isMain ? '#ffdf9e' : 'rgba(0,0,0,0.5)',
            color: isMain ? '#5a3d12' : '#e6cf8a',
            border: '0.4cqw solid rgba(230,207,138,0.7)',
          }}
        >
          ★
        </button>
      ) : null}
    </div>
  )
}

/**
 * Attack-plan modal shown after the leader picks Conquer & Recruit or Raid for Resources.
 * The leader picks a castle count (Conquer 2/4/6, Raid 5/10/15 — via tabs), then for each
 * castle selects an enemy castle on the espionage map (which fills in the enemy alliance +
 * owner), an attack window inside the season, and assigns attackers + a main attacker from
 * the alliance roster. No dismiss; the plan finalizes the season (Back returns to the picker).
 */
export const AllianceAttackPlanModal: React.FC<AllianceAttackPlanModalProps> = ({
  open,
  address,
  allianceId,
  optionId,
  period,
  onBack,
  onFinalized,
}) => {
  const { t } = useTranslation()
  const counts = ALLIANCE_TASK_ATTACK_COUNTS[optionId]
  const [count, setCount] = useState<number | null>(null)
  const [rows, setRows] = useState<TargetRow[]>([])
  const [ownMembers, setOwnMembers] = useState<AllianceWarTargetMember[]>([])
  const [pickerIndex, setPickerIndex] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)

  // "Attack from" cannot be earlier than the moment the task is set (season start floor);
  // "Attack until" cannot exceed the season end.
  const seasonMin = useMemo(
    () => toLocalInput(new Date(Math.max(Date.now(), new Date(period.periodStart).getTime()))),
    [period.periodStart],
  )
  const seasonMax = useMemo(() => toLocalInput(new Date(period.periodEnd)), [period.periodEnd])

  useEffect(() => {
    if (!open || !address || !allianceId) return
    let alive = true
    void getAllianceWarTargetMembers(address, allianceId)
      .then((r) => {
        if (alive) setOwnMembers(r.members || [])
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [open, address, allianceId])

  const pickCount = useCallback(
    (n: number) => {
      setCount(n)
      setRows(
        Array.from({ length: n }, () => ({
          allianceId: '',
          allianceName: '',
          memberWallet: '',
          ownerName: '',
          castleId: '',
          attackerWallets: [],
          mainAttackerWallet: '',
          from: seasonMin,
          to: seasonMax,
        })),
      )
    },
    [seasonMin, seasonMax],
  )

  // The first count tab (Conquer 2 / Raid 5) is selected by default when the modal opens.
  useEffect(() => {
    if (open && count === null && counts.length) pickCount(counts[0])
  }, [open, count, counts, pickCount])

  const updateRow = (i: number, patch: Partial<TargetRow>) => {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))
  }

  const toggleAttacker = (i: number, wallet: string) => {
    setRows((prev) =>
      prev.map((r, idx) => {
        if (idx !== i) return r
        const has = r.attackerWallets.includes(wallet)
        const attackerWallets = has ? r.attackerWallets.filter((w) => w !== wallet) : [...r.attackerWallets, wallet]
        const mainAttackerWallet = attackerWallets.includes(r.mainAttackerWallet) ? r.mainAttackerWallet : ''
        return { ...r, attackerWallets, mainAttackerWallet }
      }),
    )
  }

  const handlePicked = (castle: CastleData) => {
    if (pickerIndex === null) return
    // Each castle can only be targeted once — reject a castle already chosen for another row.
    if (rows.some((r, idx) => idx !== pickerIndex && r.castleId && r.castleId === castle._id)) {
      alert.open(t('alliance.conquerDupCastle', { defaultValue: 'That castle is already selected.' }))
      return
    }
    updateRow(pickerIndex, {
      allianceId: castle.allianceId || '',
      allianceName: castle.allianceName || t('alliance.conquerUnknownAlliance', { defaultValue: 'Enemy Alliance' }),
      memberWallet: castle.ownerId,
      ownerName: castle.ownerName || castle.ownerId.slice(0, 8),
      castleId: castle._id,
    })
    setPickerIndex(null)
  }

  if (!open || typeof document === 'undefined') return null

  const canSubmit =
    count !== null &&
    rows.length === count &&
    rows.every(
      (r) =>
        r.allianceId &&
        r.memberWallet &&
        r.attackerWallets.length > 0 &&
        r.mainAttackerWallet &&
        r.from &&
        r.to &&
        new Date(r.from) < new Date(r.to),
    )

  /**
   * First unmet requirement blocking the "Lock … Plan" button, so the leader is told exactly what is
   * missing instead of facing a silently greyed-out button. Mirrors the `canSubmit` checks in order.
   * Returns null when the plan is valid — or when no castle count is chosen yet, since the form
   * already shows its own "select a castle count" hint in that case.
   */
  const disabledReason: string | null = (() => {
    if (canSubmit || count === null) return null
    if (rows.length !== count) {
      return t('alliance.conquerNeedRows', {
        defaultValue: 'Plan all {{count}} target(s) to continue.',
        count,
      })
    }
    for (let i = 0; i < rows.length; i += 1) {
      const r = rows[i]
      const n = i + 1
      if (!r.allianceId || !r.memberWallet) {
        return t('alliance.conquerNeedTarget', { defaultValue: 'Target {{n}}: choose an enemy castle.', n })
      }
      if (r.attackerWallets.length === 0) {
        return t('alliance.conquerNeedAttackers', { defaultValue: 'Target {{n}}: assign at least one attacker.', n })
      }
      if (!r.mainAttackerWallet) {
        return t('alliance.conquerNeedMain', { defaultValue: 'Target {{n}}: pick a main attacker (★).', n })
      }
      if (!r.from || !r.to) {
        return t('alliance.conquerNeedWindow', { defaultValue: 'Target {{n}}: set the attack window.', n })
      }
      if (new Date(r.from) >= new Date(r.to)) {
        return t('alliance.conquerBadWindow', { defaultValue: 'Target {{n}}: end time must be after start time.', n })
      }
    }
    return null
  })()

  const handleConfirm = async () => {
    if (!canSubmit || busy || count === null) return
    setBusy(true)
    try {
      const attackTargets: AttackTargetInput[] = rows.map((r) => ({
        targetAllianceId: r.allianceId,
        targetMemberWallet: r.memberWallet,
        attackerWallets: r.attackerWallets,
        mainAttackerWallet: r.mainAttackerWallet,
        attackFrom: new Date(r.from).toISOString(),
        attackTo: new Date(r.to).toISOString(),
      }))
      const res = await finalizeAllianceTaskPeriod(address, optionId, period._id, {
        attackCount: count,
        attackTargets,
      })
      const delta = res.governanceDelta ?? 0
      alert.open(
        t('alliance.taskFinalized', {
          defaultValue: 'Official task locked. Governance {{delta}}.',
          delta: delta > 0 ? `+${delta}` : String(delta),
        }),
      )
      onFinalized()
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string }
      alert.open(e?.response?.data?.message || e?.message || t('alliance.taskFinalizeFailed', { defaultValue: 'Could not lock the task.' }))
      setBusy(false)
    }
  }

  const title =
    optionId === 'conquer'
      ? t('alliance.taskOptionConquer', { defaultValue: 'Conquer & Recruit' })
      : t('alliance.taskOptionRaid', { defaultValue: 'Raid for Resources' })
  const subtitle =
    optionId === 'conquer'
      ? t('alliance.conquerSubtitle2', { defaultValue: 'Alliance Seasonal Plan for Castle Conquest & Recruitment' })
      : t('alliance.raidSubtitle2', { defaultValue: 'Alliance Seasonal Plan for Resource Raids' })
  const planPrompt =
    optionId === 'conquer'
      ? t('alliance.conquerHowMany', { defaultValue: 'Plan how many castles to conquer this season' })
      : t('alliance.raidHowMany', { defaultValue: 'Plan how many castles to raid this season' })

  return createPortal(
    <div
      className="fixed inset-0 z-[240] flex items-start justify-center overflow-y-auto bg-black/80 p-4"
      style={{ zIndex: 240 }}
      role="presentation"
    >
      <div
        className="relative my-auto w-full select-none"
        style={{
          width: 'min(92vw, 600px)',
          containerType: 'inline-size',
          backgroundImage: `url(${MODAL_BG})`,
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
          padding: '4% 2% 1%',
          filter: 'drop-shadow(0 18px 52px rgba(0,0,0,0.7))',
        }}
        role="dialog"
        aria-modal="true"
      >
        {/* Title */}
        <h2 className="text-center" style={{ ...ALLIANCE_TASK_TITLE_STYLE, fontSize: '6cqw' }}>
          {title}
        </h2>
        <p
          className="text-center"
          style={{ fontFamily: CINZEL, fontWeight: 600, fontSize: '2.4cqw', color: '#6a4d2c', margin: '1.2cqw 0 0', letterSpacing: '0.02em' }}
        >
          {subtitle}
        </p>
        <p
          className="text-center"
          style={{ fontFamily: CINZEL, fontWeight: 700, fontSize: '2.5cqw', color: '#4a3418', margin: '2.6cqw 0 0', textTransform: 'uppercase', letterSpacing: '0.04em' }}
        >
          {planPrompt}
        </p>

        {/* Count tabs */}
        <div className="flex items-stretch justify-center" style={{ marginTop: '2.4cqw', gap: '2.5cqw' }}>
          {counts.map((n) => {
            const active = count === n
            return (
              <button
                key={n}
                type="button"
                onClick={() => pickCount(n)}
                disabled={busy}
                className="relative transition-[filter,transform] hover:brightness-110 active:scale-[0.99] disabled:cursor-not-allowed"
                style={{
                  width: '27cqw',
                  height: '10cqw',
                  backgroundImage: `url(${active ? TAB_ACTIVE : TAB_INACTIVE})`,
                  backgroundSize: '100% 100%',
                  backgroundRepeat: 'no-repeat',
                  fontFamily: CINZEL,
                  fontWeight: 800,
                  fontSize: '3cqw',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                  color: active ? '#fff2c8' : '#c8ab74',
                  textShadow: active ? '0 1px 2px rgba(30,15,4,0.9)' : 'none',
                  filter: active ? 'none' : 'brightness(0.92)',
                }}
              >
                {t('alliance.conquerNCastles', { defaultValue: '{{n}} Castles', n })}
              </button>
            )
          })}
        </div>

        {/* Castle rows — fixed-height scroll region so the modal height stays constant
            whether the leader picks 2, 4, or 6 castles (extra castles scroll inside). */}
        {count !== null ? (
          <div
            className="mt-[5cqw] flex min-w-0 flex-col overflow-y-auto"
            style={{ height: '54vh', paddingLeft: '0.5cqw', paddingRight: '2.5cqw', gap: '2cqw' }}
          >
            {rows.map((row, i) => {
              const selected = !!row.memberWallet
              return (
                <div
                  key={i}
                  className="flex min-w-0 flex-col"
                  style={{ paddingTop: '1cqw' }}
                >
                  {/* Castle banner title (plaque + trailing rule, matching the reference) */}
                  <div className="flex items-center">
                    <div
                      className="relative flex shrink-0 items-center"
                      style={{
                        height: '8.6cqw',
                        width: '46cqw',
                        backgroundImage: `url(${TITLE_BG})`,
                        backgroundSize: '100% 100%',
                        backgroundRepeat: 'no-repeat',
                        paddingLeft: '1cqw',
                      }}
                    >
                      <img
                        src={CASTLE_ICON}
                        alt=""
                        draggable={false}
                        className="shrink-0 object-contain"
                        style={{ height: '11cqw', width: 'auto', marginRight: '4cqw', filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.55))' }}
                      />
                      <span
                        style={{ fontFamily: CINZEL, fontWeight: 800, fontSize: '3.4cqw', letterSpacing: '0.05em', textTransform: 'uppercase', color: '#f6e7c0', textShadow: '0 1px 2px rgba(20,10,3,0.8)' }}
                      >
                        {t('alliance.conquerCastleN', { defaultValue: 'Castle {{n}}', n: i + 1 })}
                      </span>
                    </div>
                    <div
                      className="flex-1"
                      style={{ height: '0.3cqw', marginLeft: '1.5cqw', background: 'linear-gradient(90deg, rgba(92,62,22,0.9), rgba(92,62,22,0.15))' }}
                    />
                  </div>

                  {/* Target + select-castle */}
                  <div className="mt-[3cqw] grid grid-cols-2" style={{ gap: '7cqw' }}>
                    <div className="flex flex-col" style={{ gap: '1cqw' }}>
                      <span style={labelStyle}>{t('alliance.conquerTargetAlliance', { defaultValue: 'Target Enemy Alliance' })}</span>
                      <div
                        className="flex items-center"
                        style={{
                          height: '9cqw',
                          backgroundImage: `url(${TEXT_BG})`,
                          backgroundSize: '100% 100%',
                          backgroundRepeat: 'no-repeat',
                          padding: '0 3cqw',
                        }}
                      >
                        <span
                          className="truncate"
                          style={{ fontFamily: CINZEL, fontWeight: 700, fontSize: '2.7cqw', color: selected ? '#f3ead0' : 'rgba(226,213,178,0.4)' }}
                        >
                          {selected ? row.allianceName : t('alliance.conquerNotSelected', { defaultValue: 'Not selected' })}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col" style={{ gap: '1cqw' }}>
                      <span style={labelStyle}>{t('alliance.conquerSelectCastle', { defaultValue: 'Select Castle' })}</span>
                      <button
                        type="button"
                        onClick={() => setPickerIndex(i)}
                        disabled={busy}
                        className="flex items-center justify-center transition-[filter] hover:brightness-110 disabled:cursor-not-allowed"
                        style={{
                          height: '9cqw',
                          backgroundImage: `url(${TEXT_BG})`,
                          backgroundSize: '100% 100%',
                          backgroundRepeat: 'no-repeat',
                          padding: '0 3cqw',
                          fontFamily: CINZEL,
                          fontWeight: 800,
                          fontSize: '2.7cqw',
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                          color: '#f0d488',
                        }}
                      >
                        <span className="truncate">
                          {selected ? row.ownerName : t('alliance.conquerSelectCastle', { defaultValue: 'Select Castle' })}
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Attack window */}
                  <div className="mt-[3cqw] grid grid-cols-2" style={{ gap: '7cqw' }}>
                    <div className="flex flex-col" style={{ gap: '1cqw' }}>
                      <span style={labelStyle}>{t('alliance.conquerAttackFrom', { defaultValue: 'Attack from' })}</span>
                      <DateTimeField
                        value={row.from}
                        min={seasonMin}
                        max={seasonMax}
                        disabled={busy}
                        onChange={(v) => updateRow(i, { from: v })}
                      />
                    </div>
                    <div className="flex flex-col" style={{ gap: '1cqw' }}>
                      <span style={labelStyle}>{t('alliance.conquerAttackTo', { defaultValue: 'Attack until' })}</span>
                      <DateTimeField
                        value={row.to}
                        min={seasonMin}
                        max={seasonMax}
                        disabled={busy}
                        onChange={(v) => updateRow(i, { to: v })}
                      />
                    </div>
                  </div>

                  {/* Assign attackers */}
                  <div className="mt-[3cqw] min-w-0">
                    <span style={labelStyle}>{t('alliance.conquerAttackers', { defaultValue: 'Assign Attackers' })}</span>
                    {ownMembers.length === 0 ? (
                      <div className="mt-[1cqw] text-[2.3cqw]" style={{ fontFamily: CINZEL, color: 'rgba(226,213,178,0.45)' }}>
                        {t('alliance.conquerNoMembers', { defaultValue: 'No members to assign.' })}
                      </div>
                    ) : (
                      <div
                        className="mt-[1.4cqw] flex w-full min-w-0 overflow-x-auto"
                        style={{ scrollbarWidth: 'thin', paddingTop: '1.6cqw', paddingBottom: '1.6cqw', paddingLeft: '2.4cqw', gap: '3.5cqw' }}
                      >
                        {ownMembers.map((m) => (
                          <AttackerCard
                            key={m.walletAddress}
                            member={m}
                            picked={row.attackerWallets.includes(m.walletAddress)}
                            isMain={row.mainAttackerWallet === m.walletAddress}
                            disabled={busy}
                            onToggle={() => toggleAttacker(i, m.walletAddress)}
                            onSetMain={() => updateRow(i, { mainAttackerWallet: m.walletAddress })}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <p className="py-[3cqw] text-center" style={{ fontFamily: CINZEL, fontSize: '2.8cqw', color: 'rgba(74,52,24,0.75)' }}>
            {t('alliance.conquerPickCountFirst', { defaultValue: 'Select a castle count to begin.' })}
          </p>
        )}

        {/* Why the lock button is disabled — first unmet requirement. */}
        {disabledReason && !busy ? (
          <p
            className="mt-[2.6cqw] flex items-center justify-center text-center"
            style={{
              fontFamily: CINZEL,
              fontWeight: 700,
              fontSize: '2.5cqw',
              lineHeight: 1.3,
              letterSpacing: '0.02em',
              color: '#8a3b1e',
              gap: '1cqw',
            }}
            role="status"
          >
            <span aria-hidden style={{ fontSize: '2.9cqw' }}>⚠</span>
            {disabledReason}
          </p>
        ) : null}

        {/* Footer buttons */}
        <div className="mt-[3cqw] flex items-stretch" style={{ gap: '3cqw' }}>
          <button
            type="button"
            onClick={onBack}
            disabled={busy}
            className="transition-[filter,transform] hover:brightness-110 active:scale-[0.99] disabled:opacity-50"
            style={{
              width: '32%',
              height: '11cqw',
              backgroundImage: `url(${BACK_BTN})`,
              backgroundSize: '100% 100%',
              backgroundRepeat: 'no-repeat',
              fontFamily: CINZEL,
              fontWeight: 800,
              fontSize: '3cqw',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: '#f3ead0',
              textShadow: '0 1px 2px rgba(30,15,4,0.85)',
            }}
          >
            {t('common.back', { defaultValue: 'Back' })}
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={!canSubmit || busy}
            className="flex-1 transition-[filter,transform] hover:brightness-110 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-45"
            style={{
              height: '11cqw',
              backgroundImage: `url(${CONFIRM_BTN})`,
              backgroundSize: '100% 100%',
              backgroundRepeat: 'no-repeat',
              fontFamily: CINZEL,
              fontWeight: 800,
              fontSize: '3.2cqw',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: '#fff2c8',
              textShadow: '0 1px 2px rgba(30,15,4,0.9)',
            }}
          >
            {busy
              ? t('common.loading', { defaultValue: 'Loading...' })
              : optionId === 'conquer'
                ? t('alliance.conquerConfirm', { defaultValue: 'Lock Conquer Plan' })
                : t('alliance.raidConfirm', { defaultValue: 'Lock Raid Plan' })}
          </button>
        </div>
      </div>

      <CastlePickerModal
        open={pickerIndex !== null}
        address={address}
        ownAllianceId={allianceId}
        selectedCastleId={pickerIndex !== null ? rows[pickerIndex]?.castleId : null}
        onSelect={handlePicked}
        onClose={() => setPickerIndex(null)}
      />
    </div>,
    document.body,
  )
}

export default AllianceAttackPlanModal
